import { Component, OnInit, Inject } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Certification, CertificationType, CertificationAttachment } from 'app/profile/models/profile.model';
import { Observable } from 'rxjs';
import { ProfileService } from 'app/profile/services/profile.service';
import { I18nService } from 'app/shared/services/i18n.service';
import { TslDialog, TslOption } from '@tesla/ng-tds';
import { StateService } from 'app/profile/services/state.service';
import { Status, StatusEnum } from 'app/profile/models/state.model';
import * as FileSaver from 'file-saver';
import { Store } from '@ngrx/store';
import { GeneralSettingsState } from 'app/profile/store/state/general.state';
import { GeneralSettings } from 'app/profile/models/generic.model';
import { getGeneralSettings } from 'app/profile/store/app.selector';

@Component({
    selector: 'app-certification-dialog',
    templateUrl: './certification-dialog.component.html',
    styleUrls: ['./certification-dialog.component.scss']
})
export class CertificationDialogComponent implements OnInit {
    deleteTitle: string;
    deleteConfirmation: string;
    certificationTypes: CertificationType[];
    certForm: FormGroup;
    settings: GeneralSettings;
    maxDate: Date;

    get type() { return this.certForm.get('type'); }
    get issued() { return this.certForm.get('issued'); }
    get expired() { return this.certForm.get('expired'); }
    get issuer() { return this.certForm.get('issuer'); }

    get fileSizeLimitMB() { return this.settings.fileSizeLimitMB * 1000000; }

    constructor(private fb: FormBuilder,
        private translate: I18nService,
        private stateService: StateService,
        private service: ProfileService,
        private dialog: TslDialog,
        private store: Store<GeneralSettingsState>,
        public dialogRef: MatDialogRef<CertificationDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: Certification) {
        this.certForm = this.fb.group({
            type: ['', Validators.required],
            issued: ['', Validators.required],
            expired: [''],
            issuer: ['', Validators.required]
        });
        this.maxDate = new Date();
    }

    ngOnInit() {
        this.data.newFiles = [];

        this.loadResources();
    }

    onClose(): void {
        this.dialogRef.close();
    }

    onSave(): void {
        if (this.certForm.invalid) {
            return;
        }

        const selectedType = this.certificationTypes.find(x => x.type === this.type.value);
        if (!selectedType) {
            const newCertificationType = new CertificationType();
            newCertificationType.type = this.type.value;
            this.service.postCertificationType(this.data.supplierCode, newCertificationType)
                .subscribe(res => {
                    this.data.certificationTypeId = res.id;
                    this.data.certificationType = res.type;
                    this.data.certifiedDate = new Date(this.certForm.value.issued);
                    this.data.expirationDate = this.certForm.value.expired ? new Date(this.certForm.value.expired) : null;
                    this.data.certifierName = this.certForm.value.issuer;
                    this.dialogRef.close(this.data);
                });
        } else {
            this.data.certificationTypeId = selectedType.id;
            this.data.certifiedDate = new Date(this.certForm.value.issued);
            this.data.expirationDate = this.certForm.value.expired ? new Date(this.certForm.value.expired) : null;
            this.data.certifierName = this.certForm.value.issuer;
            this.dialogRef.close(this.data);
        }
    }

    onAddFile(file: File): void {
        if (file.size > this.fileSizeLimitMB) {
            this.dialog.alert('Alert', this.translate.get('File size can not be more than 10MB!'));
            return;
        }

        this.data.newFiles.push(file);
    }

    onDeleteAttachment(attach: CertificationAttachment): void {
        this.dialog.confirm(this.translate.get(this.deleteTitle), this.translate.get(this.deleteConfirmation))
            .afterClosed()
            .subscribe(confirmed => {
                if (confirmed) {
                    this.service.deleteCertificationAttachment(this.data.supplierCode, attach.id)
                        .subscribe(_ => {
                            const attachIndex = this.data.attachments.indexOf(attach);
                            this.data.attachments.splice(attachIndex, 1);
                            this.stateService.currentStatus.next(new Status(StatusEnum.Success));
                        });
                }
            });
    }

    onDeleteFile(file: File): void {
        const fileIndex = this.data.newFiles.indexOf(file);
        this.data.newFiles.splice(fileIndex, 1);
    }

    onDownloadAttachment(attach: CertificationAttachment): void {
        this.service.downloadCertificationAttachment(this.data.supplierCode, attach.storageKey)
            .subscribe(file => {
                FileSaver.saveAs(file, attach.fileName);
            });
    }

    onDownloadFile(file: File): File {
        return file;
    }

    getFileType(filename: string): string {
        const nameArray = filename.split(".");
        return nameArray[nameArray.length - 1];
    }

    onSelectType(option: TslOption): void {
        const type: CertificationType = option.value;
        this.type.setValue(type.type);
    }

    private loadResources(): void {
        this.store.select(getGeneralSettings).subscribe(res => this.settings = res);
        this.service.getCertificationTypes(this.data.supplierCode).subscribe(res => {
            this.certificationTypes = res;
            if (this.data.id) {
                this.certForm.patchValue({
                    type: this.certificationTypes.find(x => x.id == this.data.certificationTypeId).type,
                    issued: this.data.certifiedDate,
                    expired: this.data.expirationDate,
                    issuer: this.data.certifierName
                });
            }
        });

        this.translate.getAsync('Delete Item')
            .subscribe(t => this.deleteTitle = t);

        this.translate.getAsync('Are you sure you want to delete this item?')
            .subscribe(t => this.deleteConfirmation = t);
    }
}
